import { useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    ArrowLeft,
    Check,
    Loader2,
    Car,
    TriangleAlert,
    Stethoscope,
    Dog,
    MountainSnow,
    HardHat,
    CircleHelp,
} from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { ATTORNEY } from "@/data/content";

const FORMSPREE = process.env.REACT_APP_FORMSPREE_ENDPOINT || "";

const STEPS = [
    {
        key: "incidentType",
        title: "What kind of incident was it?",
        options: [
            { label: "Car or Truck Accident", icon: Car },
            { label: "Slip / Trip & Fall", icon: TriangleAlert },
            { label: "Medical or Dental Malpractice", icon: Stethoscope },
            { label: "Dog Bite / Animal Attack", icon: Dog },
            { label: "Ski or Snowboard Accident", icon: MountainSnow },
            { label: "Workplace Accident", icon: HardHat },
            { label: "Something Else / Not Sure", icon: CircleHelp },
        ],
    },
    {
        key: "policeReport",
        title: "Was a police report filed?",
        options: [{ label: "Yes" }, { label: "No" }, { label: "Not sure" }],
    },
    {
        key: "insuranceClaim",
        title: "Have you made an insurance claim?",
        note: "Either yours or the other side's — and don't worry, a claim already in progress doesn't hurt you.",
        options: [{ label: "Yes" }, { label: "No" }, { label: "Not sure" }],
    },
    {
        key: "workingAtTime",
        title: "Were you working at the time of the accident?",
        note: "If yes, you may have both a workers' compensation claim and a personal injury claim — we check both.",
        options: [{ label: "Yes" }, { label: "No" }, { label: "Not sure" }],
    },
    {
        key: "location",
        title: "Where did it happen?",
        options: [
            { label: "Sacramento County" },
            { label: "Placer County (Roseville, Rocklin, Auburn…)" },
            { label: "Elsewhere in California" },
            { label: "Outside California" },
        ],
    },
    {
        key: "timeframe",
        title: "When did it happen?",
        note: "California deadlines can be as short as six months for some claims — the honest answer here matters more than any other.",
        options: [
            { label: "Within the last 6 months" },
            { label: "6–12 months ago" },
            { label: "1–2 years ago" },
            { label: "More than 2 years ago" },
        ],
    },
    {
        key: "treatment",
        title: "Have you gotten medical treatment?",
        options: [
            { label: "Yes — still treating" },
            { label: "Yes — finished treatment" },
            { label: "Not yet, but planning to" },
            { label: "No treatment" },
        ],
    },
];

const LABELS = {
    incidentType: "Incident",
    policeReport: "Police report",
    insuranceClaim: "Insurance claim",
    workingAtTime: "Working",
    location: "Location",
    timeframe: "When",
    treatment: "Treatment",
};

export default function CaseEvaluator() {
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState({});
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const totalSteps = STEPS.length + 1; // questions + contact
    const onContactStep = step === STEPS.length;
    const current = STEPS[step];

    function pick(key, value) {
        setAnswers((a) => ({ ...a, [key]: value }));
    }

    async function onSubmit(e) {
        e.preventDefault();
        const form = e.currentTarget;
        const data = new FormData(form);

        if (!FORMSPREE || FORMSPREE.includes("YOUR_FORM_ID")) {
            toast.error("Form endpoint not configured.");
            return;
        }

        setSending(true);
        try {
            const res = await fetch(FORMSPREE, {
                method: "POST",
                body: data,
                headers: { Accept: "application/json" },
            });
            if (res.ok) {
                setSent(true);
                form.reset();
                toast.success("Case review received — Joe will be in touch shortly.");
            } else {
                const j = await res.json().catch(() => ({}));
                toast.error(
                    j?.errors?.[0]?.message ||
                        "Something went wrong. Please call (916) 492-2000.",
                );
            }
        } catch (err) {
            toast.error("Network error. Please try again or call (916) 492-2000.");
        } finally {
            setSending(false);
        }
    }

    return (
        <>
            <SEO
                title="60-Second Case Evaluator — Do You Have a Case? | Joseph G. Helfrick"
                description="Answer seven quick questions about your injury and get your free case review in front of Sacramento personal injury attorney Joseph G. Helfrick with the details he needs. Confidential. No fee unless we win."
                path="/case-evaluator"
                breadcrumbs={[
                    { name: "Home", path: "/" },
                    { name: "Case Evaluator", path: "/case-evaluator" },
                ]}
            />

            <section
                data-testid="evaluator-hero"
                className="border-b border-[#1F3458]"
            >
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-12 lg:pt-28 lg:pb-16">
                    <div className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] font-bold">
                        60-Second Case Evaluator
                    </div>
                    <h1 className="mt-6 font-serif text-5xl lg:text-7xl text-[#F7F5EB] leading-[0.95] font-light max-w-4xl">
                        Seven questions.{" "}
                        <span className="italic text-[#D4AF37]">
                            Then a real attorney reads it.
                        </span>
                    </h1>
                    <p className="mt-8 text-lg text-[#9FB3D4] max-w-3xl leading-relaxed">
                        No chatbot verdicts, no gimmicks. Your answers go
                        directly to Joe with the details that matter, and you
                        get a straight answer — free and confidential.
                    </p>
                </div>
            </section>

            <section className="border-b border-[#1F3458]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
                    <div className="max-w-3xl mx-auto">
                        {sent ? (
                            <div
                                data-testid="evaluator-success"
                                className="border border-[#D4AF37] bg-[#11203D] p-8 md:p-12"
                            >
                                <div className="font-serif text-3xl text-[#D4AF37]">
                                    Case review received.
                                </div>
                                <p className="mt-4 text-[#F7F5EB]/90 leading-relaxed">
                                    Joe will review your answers and reach out
                                    within one business day. If your matter is
                                    urgent — or your incident was more than a
                                    year ago — don't wait on email: call{" "}
                                    <a
                                        href={`tel:${ATTORNEY.phone.replace(/[^0-9]/g, "")}`}
                                        className="gold-link"
                                    >
                                        {ATTORNEY.phone}
                                    </a>{" "}
                                    now, because legal deadlines keep running.
                                </p>
                                <Link
                                    to="/blog"
                                    data-testid="evaluator-success-blog"
                                    className="mt-6 inline-flex items-center gap-2 text-[#D4AF37] text-sm font-bold uppercase tracking-wider hover:text-[#B8962A] transition-colors"
                                >
                                    Read our case guides while you wait{" "}
                                    <ArrowRight size={14} />
                                </Link>
                            </div>
                        ) : (
                            <div className="bg-[#11203D] border border-[#1F3458] p-8 md:p-12">
                                {/* Progress */}
                                <div
                                    data-testid="evaluator-progress"
                                    className="mb-10"
                                >
                                    <div className="flex justify-between text-[10px] uppercase tracking-[0.22em] text-[#9FB3D4] font-bold">
                                        <span>
                                            Step {step + 1} of {totalSteps}
                                        </span>
                                        <span>
                                            {onContactStep
                                                ? "Almost done"
                                                : "60 seconds"}
                                        </span>
                                    </div>
                                    <div className="mt-3 h-[3px] bg-[#1F3458]">
                                        <div
                                            className="h-full bg-[#D4AF37] transition-[width] duration-500"
                                            style={{
                                                width: `${((step + 1) / totalSteps) * 100}%`,
                                            }}
                                        />
                                    </div>
                                </div>

                                {!onContactStep ? (
                                    <div key={current.key}>
                                        <h2
                                            data-testid="evaluator-step-title"
                                            className="font-serif text-3xl md:text-4xl text-[#F7F5EB] leading-tight"
                                        >
                                            {current.title}
                                        </h2>
                                        {current.note && (
                                            <p className="mt-4 text-sm text-[#9FB3D4] leading-relaxed">
                                                {current.note}
                                            </p>
                                        )}
                                        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {current.options.map((opt, i) => {
                                                const selected =
                                                    answers[current.key] ===
                                                    opt.label;
                                                const Icon = opt.icon;
                                                return (
                                                    <button
                                                        key={opt.label}
                                                        type="button"
                                                        data-testid={`evaluator-option-${step}-${i}`}
                                                        onClick={() =>
                                                            pick(
                                                                current.key,
                                                                opt.label,
                                                            )
                                                        }
                                                        className={`flex items-center gap-4 text-left px-5 py-4 border transition-colors ${
                                                            selected
                                                                ? "border-[#D4AF37] bg-[#0B1A36] text-[#F7F5EB]"
                                                                : "border-[#1F3458] text-[#9FB3D4] hover:border-[#D4AF37]/60 hover:text-[#F7F5EB]"
                                                        }`}
                                                    >
                                                        {Icon && (
                                                            <Icon
                                                                size={18}
                                                                className={
                                                                    selected
                                                                        ? "text-[#D4AF37] shrink-0"
                                                                        : "text-[#9FB3D4] shrink-0"
                                                                }
                                                            />
                                                        )}
                                                        <span className="text-sm font-medium flex-1">
                                                            {opt.label}
                                                        </span>
                                                        {selected && (
                                                            <Check
                                                                size={16}
                                                                className="text-[#D4AF37] shrink-0"
                                                            />
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <div className="mt-10 flex items-center justify-between">
                                            {step > 0 ? (
                                                <button
                                                    type="button"
                                                    data-testid="evaluator-back"
                                                    onClick={() =>
                                                        setStep(step - 1)
                                                    }
                                                    className="inline-flex items-center gap-2 text-[#9FB3D4] text-sm font-bold uppercase tracking-wider hover:text-[#D4AF37] transition-colors"
                                                >
                                                    <ArrowLeft size={14} />
                                                    Back
                                                </button>
                                            ) : (
                                                <span />
                                            )}
                                            <button
                                                type="button"
                                                data-testid="evaluator-continue"
                                                disabled={!answers[current.key]}
                                                onClick={() =>
                                                    setStep(step + 1)
                                                }
                                                className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#07142B] font-bold uppercase tracking-wider text-sm px-8 py-4 hover:bg-[#B8962A] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                Continue
                                                <ArrowRight size={16} />
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <h2 className="font-serif text-3xl md:text-4xl text-[#F7F5EB] leading-tight">
                                            Where should Joe reach you?
                                        </h2>

                                        {/* Summary of answers */}
                                        <div
                                            data-testid="evaluator-summary"
                                            className="mt-6 flex flex-wrap gap-2"
                                        >
                                            {STEPS.map((s) => (
                                                <button
                                                    key={s.key}
                                                    type="button"
                                                    data-testid={`evaluator-summary-${s.key}`}
                                                    onClick={() =>
                                                        setStep(
                                                            STEPS.indexOf(s),
                                                        )
                                                    }
                                                    title="Click to change"
                                                    className="text-xs border border-[#1F3458] text-[#9FB3D4] px-3 py-2 hover:border-[#D4AF37]/60 hover:text-[#F7F5EB] transition-colors"
                                                >
                                                    <span className="text-[#D4AF37] font-bold uppercase tracking-wider mr-2">
                                                        {LABELS[s.key]}:
                                                    </span>
                                                    {answers[s.key]}
                                                </button>
                                            ))}
                                        </div>

                                        <form
                                            data-testid="evaluator-form"
                                            onSubmit={onSubmit}
                                            className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5"
                                        >
                                            <div>
                                                <label
                                                    htmlFor="eval-firstName"
                                                    className="block text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-2"
                                                >
                                                    First Name *
                                                </label>
                                                <input
                                                    id="eval-firstName"
                                                    name="firstName"
                                                    required
                                                    data-testid="evaluator-first-name"
                                                    className="w-full px-4 py-3 text-base"
                                                />
                                            </div>
                                            <div>
                                                <label
                                                    htmlFor="eval-lastName"
                                                    className="block text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-2"
                                                >
                                                    Last Name *
                                                </label>
                                                <input
                                                    id="eval-lastName"
                                                    name="lastName"
                                                    required
                                                    data-testid="evaluator-last-name"
                                                    className="w-full px-4 py-3 text-base"
                                                />
                                            </div>
                                            <div>
                                                <label
                                                    htmlFor="eval-email"
                                                    className="block text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-2"
                                                >
                                                    Email *
                                                </label>
                                                <input
                                                    id="eval-email"
                                                    name="email"
                                                    type="email"
                                                    required
                                                    data-testid="evaluator-email"
                                                    className="w-full px-4 py-3 text-base"
                                                />
                                            </div>
                                            <div>
                                                <label
                                                    htmlFor="eval-phone"
                                                    className="block text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-2"
                                                >
                                                    Phone *
                                                </label>
                                                <input
                                                    id="eval-phone"
                                                    name="phone"
                                                    type="tel"
                                                    required
                                                    data-testid="evaluator-phone"
                                                    className="w-full px-4 py-3 text-base"
                                                />
                                            </div>
                                            <div className="md:col-span-2">
                                                <label
                                                    htmlFor="eval-message"
                                                    className="block text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-2"
                                                >
                                                    Anything else Joe should
                                                    know? (optional)
                                                </label>
                                                <textarea
                                                    id="eval-message"
                                                    name="message"
                                                    rows={4}
                                                    data-testid="evaluator-message"
                                                    className="w-full px-4 py-3 text-base resize-y"
                                                    placeholder="A sentence or two about what happened and your injuries."
                                                />
                                            </div>

                                            {/* Quiz answers ride along */}
                                            {STEPS.map((s) => (
                                                <input
                                                    key={s.key}
                                                    type="hidden"
                                                    name={s.key}
                                                    value={answers[s.key] || ""}
                                                />
                                            ))}
                                            <input
                                                type="text"
                                                name="_gotcha"
                                                tabIndex="-1"
                                                autoComplete="off"
                                                style={{ display: "none" }}
                                                aria-hidden="true"
                                            />
                                            <input
                                                type="hidden"
                                                name="_subject"
                                                value="New 60-second case evaluator submission"
                                            />

                                            <div className="md:col-span-2 flex flex-col gap-4 mt-2">
                                                <p className="text-xs text-[#9FB3D4] leading-relaxed">
                                                    Submitting this form does
                                                    not create an
                                                    attorney-client
                                                    relationship. Please do not
                                                    include confidential
                                                    information until we have
                                                    agreed in writing to
                                                    represent you.
                                                </p>
                                                <div className="flex items-center justify-between">
                                                    <button
                                                        type="button"
                                                        data-testid="evaluator-back-contact"
                                                        onClick={() =>
                                                            setStep(
                                                                STEPS.length -
                                                                    1,
                                                            )
                                                        }
                                                        className="inline-flex items-center gap-2 text-[#9FB3D4] text-sm font-bold uppercase tracking-wider hover:text-[#D4AF37] transition-colors"
                                                    >
                                                        <ArrowLeft size={14} />
                                                        Back
                                                    </button>
                                                    <button
                                                        type="submit"
                                                        disabled={sending}
                                                        data-testid="evaluator-submit"
                                                        className="inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-[#07142B] font-bold uppercase tracking-wider text-sm px-8 py-4 hover:bg-[#B8962A] transition-colors disabled:opacity-60"
                                                    >
                                                        {sending ? (
                                                            <>
                                                                <Loader2
                                                                    size={16}
                                                                    className="animate-spin"
                                                                />
                                                                Sending…
                                                            </>
                                                        ) : (
                                                            <>
                                                                Send My Free
                                                                Case Review
                                                                <ArrowRight
                                                                    size={16}
                                                                />
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                )}
                            </div>
                        )}

                        <p className="mt-6 text-xs text-[#9FB3D4] text-center leading-relaxed">
                            Prefer to skip the questions?{" "}
                            <Link to="/contact" className="gold-link">
                                Use the standard contact form
                            </Link>{" "}
                            or call{" "}
                            <a
                                href={`tel:${ATTORNEY.phone.replace(/[^0-9]/g, "")}`}
                                className="gold-link"
                            >
                                {ATTORNEY.phone}
                            </a>
                            .
                        </p>
                    </div>
                </div>
            </section>
        </>
    );
}
